import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import { SIDES_DISTANCE } from '@/shared/constants/configurator';
import { DISPLAY_SCALE } from '@/shared/constants/editor';
import { calculateConfiguratorDepth, calculateConfiguratorWidth } from '@/shared/lib/configurator';
import { Size } from '@/shared/ui/size/size';
import { PlusIcon } from 'lucide-react';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { TOP_EDITOR_TOOLS_WIDTH } from '../../constants/editor.constants';
import { TopEditorSectionsConnection } from '../top-editor-sections-connection/top-editor-sections-connection';
import { TopEditorSections } from '../top-editor-sections/top-editor-sections';
import styles from './top-editor.module.css';

export const TopEditor = () => {
	const {
		state: { doubleSided, wallMounted, sections },
		updateOptions,
	} = useConfigurator();
	const { t } = useTranslation();

	const depth = calculateConfiguratorDepth(sections, doubleSided);
	const width = calculateConfiguratorWidth(sections, wallMounted);

	const heightSizeStyles = (() => {
		const styles: CSSProperties = {
			height: depth * DISPLAY_SCALE,
		};

		if (!doubleSided) {
			const transformPx = (SIDES_DISTANCE * DISPLAY_SCALE + 40) / 2; // 40px - button height
			styles.transform = `translateY(calc(-50% + ${transformPx}px))`;
		}

		return styles;
	})();

	const widthSizeStyles = (() => {
		const totalEditorHeight = doubleSided
			? depth * DISPLAY_SCALE
			: (depth + SIDES_DISTANCE) * DISPLAY_SCALE + 40;
		const transformPx = totalEditorHeight / 2 + 40;

		const styles: CSSProperties = {
			transform: `translateY(calc(50% + ${transformPx}px))`,
			width: width * DISPLAY_SCALE,
		};

		return styles;
	})();

	return (
		<div
			style={{
				width: width * DISPLAY_SCALE + TOP_EDITOR_TOOLS_WIDTH + 40,
			}}
			className={styles.wrapper}
		>
			<Size
				type="height"
				value={`${depth} ${t('units.mm')}`}
				label={t('size.depth.rackTitle')}
				style={heightSizeStyles}
				className={styles.depthSize}
			/>
			<Size
				type="width"
				value={`${width} ${t('units.mm')}`}
				label={t('size.width.rackTitle')}
				className={styles.widthSize}
				style={widthSizeStyles}
			/>
			<div className={styles.sectionsWrapper}>
				{doubleSided ? (
					<>
						<TopEditorSections side="top" />
						<TopEditorSectionsConnection />
					</>
				) : (
					<div
						style={{ marginBottom: SIDES_DISTANCE * DISPLAY_SCALE }}
						className={styles.editorActions}
					>
						<button
							className={styles.addSectionButton}
							onClick={() => updateOptions({ doubleSided: true })}
						>
							<PlusIcon size={20} color="white" />
						</button>
					</div>
				)}

				<TopEditorSections side="bottom" />
			</div>
		</div>
	);
};
